import type { AnchorHTMLAttributes, ReactNode, Ref } from 'react';
import type { AppLinkProps } from '@/components/elementary/AppLink/types';
import { buttonVariants, OAuthSplitLayout } from '@/components/ui/button';
import { openCookiePreferencesModal } from '@/lib/analytics/cookie-consent';
import { extractTextFromChildren } from '@/lib/general-helpers';
import { cn } from '@/lib/styles';
const SOCIAL_LOGIN_URL = 'https://app.lokalise.com/login/social' as const;

// query params that should be persisted across navigation
const allowedTrackingParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'utm_campaign_id', 'utm_partner_source', 'gclid', 'li_fat_id', '_ga'];
const getTrackingParams = (params: Pick<URLSearchParams, 'get'>) => {
  const filteredParams = new URLSearchParams();
  for (const key of allowedTrackingParams) {
    const value = params.get(key);
    if (value !== null) {
      // Prevent malformed tracking params from accumulating encoded trailing slashes across navigations
      filteredParams.set(key, value.replace(/\/+$/g, ''));
    }
  }
  return filteredParams;
};
export const createQueryString = (params: Pick<URLSearchParams, 'get'>) => {
  const result = getTrackingParams(params).toString();
  return result ? `?${result}` : '';
};
export const buildInternalHref = (href: string, params: Pick<URLSearchParams, 'get'>, origin: string) => {
  const url = new URL(href, origin);
  for (const [key, value] of getTrackingParams(params)) {
    url.searchParams.set(key, value);
  }
  return `${url.pathname}${url.search}${url.hash}`;
};
export const getAnchorHref = (href: AppLinkProps['href'], isExternal: boolean, queryString = '') => isExternal ? `${new URL(href as string)}${queryString}` : `${href}${queryString}`;
export function resolveAppLinkFields<Props extends {
  download?: unknown;
}>({
  href,
  link,
  label,
  openExternalInNewTab,
  hrefMode,
  props
}: Pick<AppLinkProps, 'href' | 'link' | 'label' | 'openExternalInNewTab' | 'hrefMode'> & {
  props: Props;
}) {
  const resolvedHref = href ?? link?.href;
  if (!resolvedHref) return undefined;
  return {
    href: resolvedHref,
    label: label ?? link?.label ?? undefined,
    hrefMode: hrefMode ?? link?.hrefMode ?? 'unlocalized',
    openExternalInNewTab: openExternalInNewTab ?? link?.newTab ?? false,
    props: link?.download && props.download == null ? {
      ...props,
      download: true
    } : props
  };
}
export const getLinkExceptions = (options: {
  href: AppLinkProps['href'];
  ref: Ref<HTMLAnchorElement>;
  linkClassName: string;
  props: AnchorHTMLAttributes<HTMLAnchorElement>;
}, renderContent: () => ReactNode) => {
  const {
    href,
    ref,
    linkClassName,
    props
  } = options;
  if (typeof href === 'string' && props.download) {
    return <a href={href} ref={ref} download className={linkClassName} {...props}>
				{renderContent()}
			</a>;
  }
  if (typeof href === 'string' && href === '#intercom') {
    return <a href={href} ref={ref} onClick={e => {
      e.preventDefault();
      if (window.Intercom) {
        window.intercomOpenSource = 'cta_link';
        window.Intercom('show');
      }
    }} className={linkClassName} {...props}>
				{renderContent()}
			</a>;
  }
  if (typeof href === 'string' && href === '#cookie-settings') {
    return <a href={href} ref={ref} onClick={e => {
      e.preventDefault();
      openCookiePreferencesModal();
    }} className={linkClassName} {...props}>
				{renderContent()}
			</a>;
  }
  return undefined;
};
export const renderContent = (props: Pick<AppLinkProps, 'variant' | 'iconStart' | 'iconEnd' | 'label' | 'children'>) => {
  // children own the visible content; the resolved CMS label still feeds variant and tracking
  const visibleLabel = props.children ? undefined : props.label;
  return <>
			{props.variant === 'google' ? <OAuthSplitLayout>{props.children || props.label}</OAuthSplitLayout> : <>
					{props.iconStart && <div className="flex items-center gap-1">
							<span className="mr-0 group-hover/app-link:mr-1 group-hover/image-link:mr-1 transition-[margin] duration-200">
								{props.iconStart}
							</span>
						</div>}
					{props.children}
					{(visibleLabel || props.iconEnd) && <div className="flex items-center gap-1">
							{visibleLabel && <div className="flex justify-between">{visibleLabel}</div>}
							{props.iconEnd && <span className="transition-transform duration-200 group-hover/app-link:translate-x-1 group-hover/image-link:translate-x-1">
									{props.iconEnd}
								</span>}
						</div>}
				</>}
		</>;
};
const isSignUpWithGoogleLink = (href: NonNullable<AppLinkProps['href']>, label: AppLinkProps['label']) => href.toString().startsWith(SOCIAL_LOGIN_URL) && label?.toLowerCase().includes('google');
export const determineLinkVariant = (href: NonNullable<AppLinkProps['href']>, label: AppLinkProps['label'], variant: AppLinkProps['variant']) => isSignUpWithGoogleLink(href, label) ? 'google' : variant || 'primaryLink';
export function buildLinkClassName({
  variant,
  size,
  inverted,
  className,
  underline
}: {
  variant: AppLinkProps['variant'];
  size: AppLinkProps['size'];
  inverted: AppLinkProps['inverted'];
  className: string | undefined;
  underline: boolean;
}) {
  const isOAuth = variant === 'google';
  return cn('group/app-link flex relative flex-row items-center gap-1 h-fit', underline && 'group-hover/image-link:border-b-brand-black hover:border-b-brand-black', isOAuth ? 'group/oauth' : 'border-b border-transparent', cn(buttonVariants({
    variant,
    size,
    inverted
  }), className));
}

/**
 * Reads linkUrl and linkText from the live DOM anchor element so that any
 * DOM mutations are reflected in tracked values.
 * Falls back to the original React props if the DOM values are empty.
 */
export const getLiveTrackingProps = (anchorEl: HTMLAnchorElement, fallback: {
  href: AppLinkProps['href'];
  label?: string;
  children?: ReactNode;
}) => ({
  linkUrl: anchorEl.href || String(fallback.href),
  linkText: anchorEl.textContent?.trim() || fallback.label || extractTextFromChildren(fallback.children)
});